import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { Waves, BarChart3, Table, Users, ShoppingCart, Plus, Settings, LogOut, Menu, Microscope, Satellite, ChevronLeft, ChevronDown, Home, Fish, History, Wrench, MessageSquare } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

export default function AdminNavigation() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  
  // Check screen size and collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  const isActive = (path) => {
    if (path === '/admin' && location.pathname === '/admin') return true;
    if (path !== '/admin' && location.pathname.startsWith(path)) return true;
    return false;
  };
  
  const [toolsOpen, setToolsOpen] = useState(false);

  // Check if a tools sub-route is active
  useEffect(() => {
    if (location.pathname.startsWith('/admin/plankton') || location.pathname.startsWith('/admin/satellite')) {
      setToolsOpen(true);
    }
  }, [location.pathname]);
  
  const navItems = [
    { path: '/admin', icon: Home, label: 'Oversikt' },
    { path: '/admin/bulk-history', icon: Table, label: 'Prøver' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analyse' },
    { path: '/admin/fish-health', icon: Fish, label: 'Observasjonar' },
    { path: '/admin/users', icon: Users, label: 'Kunder' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Bestillingar' },
    { path: '/admin/audit-log', icon: History, label: 'Endringslogg' },
    { path: '/admin/chat', icon: MessageSquare, label: 'AI Chat' },
  ];

  const toolItems = [
    { path: '/admin/plankton', icon: Microscope, label: 'Plankton ID' },
    { path: '/admin/satellite-monitoring', icon: Satellite, label: 'Satellitt' },
  ];
  
  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo + collapse toggle */}
      <div className={`border-b border-slate-100 ${collapsed && !isMobile ? 'py-3 px-2' : 'p-4'}`}>
        <div className={`flex items-center ${collapsed && !isMobile ? 'flex-col gap-2' : 'justify-between'}`}>
          <Link to="/admin" className={`flex items-center gap-2.5 min-w-0 ${collapsed && !isMobile ? 'justify-center' : ''}`}>
            <div className="w-9 h-9 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Waves className="w-5 h-5 text-white" />
            </div>
            {(!collapsed || isMobile) && (
              <div className="min-w-0">
                <span className="font-semibold text-slate-900">Microsafe</span>
                <span className="block text-xs text-slate-400">Admin</span>
              </div>
            )}
          </Link>
          {!isMobile && (
            <button 
              onClick={() => setCollapsed(!collapsed)}
              className={`p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors hidden lg:flex flex-shrink-0 ${collapsed ? '' : ''}`}
              title={collapsed ? 'Utvid meny' : 'Minimer meny'}
            >
              <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>
      
      {/* New Sample Button */}
      <div className="p-3">
        <Link
          to="/admin/samples/bulk?new=1"
          className={`flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors ${
            collapsed && !isMobile ? 'justify-center p-2.5' : 'px-4 py-2.5'
          }`}
        >
          <Plus className="w-4 h-4" />
          {(!collapsed || isMobile) && <span className="font-medium text-sm">Ny prøve</span>}
        </Link>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
        {navItems.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
              isActive(path)
                ? 'bg-teal-50 text-teal-700 font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            } ${collapsed && !isMobile ? 'justify-center' : ''}`}
            title={collapsed && !isMobile ? label : undefined}
          >
            <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive(path) ? 'text-teal-600' : ''}`} />
            {(!collapsed || isMobile) && <span>{label}</span>}
          </Link>
        ))}

        {/* Tools group */}
        {(!collapsed || isMobile) ? (
          <div>
            <button
              onClick={() => setToolsOpen(!toolsOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-all text-sm ${
                toolItems.some(t => isActive(t.path))
                  ? 'bg-teal-50 text-teal-700 font-medium'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <Wrench className={`w-[18px] h-[18px] flex-shrink-0 ${toolItems.some(t => isActive(t.path)) ? 'text-teal-600' : ''}`} />
                <span>Verktøy</span>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${toolsOpen ? '' : '-rotate-90'}`} />
            </button>
            {toolsOpen && (
              <div className="ml-4 mt-0.5 space-y-0.5">
                {toolItems.map(({ path, icon: Icon, label }) => (
                  <Link
                    key={path}
                    to={path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                      isActive(path)
                        ? 'bg-teal-50 text-teal-700 font-medium'
                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive(path) ? 'text-teal-600' : ''}`} />
                    <span>{label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          toolItems.map(({ path, icon: Icon, label }) => (
            <Link
              key={path}
              to={path}
              className={`flex items-center justify-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                isActive(path) ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-slate-50'
              }`}
              title={label}
            >
              <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive(path) ? 'text-teal-600' : ''}`} />
            </Link>
          ))
        )}
      </nav>
      
      {/* Settings - pinned above user */}
      <div className="px-3 pb-1">
        <Link
          to="/admin/settings"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
            isActive('/admin/settings')
              ? 'bg-teal-50 text-teal-700 font-medium'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          } ${collapsed && !isMobile ? 'justify-center' : ''}`}
          title={collapsed && !isMobile ? 'Innstillingar' : undefined}
        >
          <Settings className={`w-[18px] h-[18px] flex-shrink-0 ${isActive('/admin/settings') ? 'text-teal-600' : ''}`} />
          {(!collapsed || isMobile) && <span>Innstillingar</span>}
        </Link>
      </div>

      {/* User */}
      <div className="p-3 border-t border-slate-100">
        <div className={`flex items-center gap-3 p-2 rounded-lg ${collapsed && !isMobile ? 'justify-center' : ''}`}>
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-teal-100 text-teal-700 text-xs font-medium">
              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {(!collapsed || isMobile) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          )}
        </div>
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 w-full px-3 py-2 mt-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ${
            collapsed && !isMobile ? 'justify-center' : ''
          }`}
          title={collapsed && !isMobile ? 'Logg ut' : undefined}
        >
          <LogOut className="w-4 h-4" />
          {(!collapsed || isMobile) && <span>Logg ut</span>}
        </button>
      </div>
    </div>
  );
  
  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col fixed left-0 top-0 h-screen bg-white border-r border-slate-100 z-40 transition-all duration-300 ${
        collapsed ? 'w-[70px]' : 'w-[240px]'
      }`}>
        <SidebarContent />
      </aside>
      
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-100 z-50 flex items-center justify-between px-4">
        <Link to="/admin" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
            <Waves className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900">Microsafe</span>
        </Link>
        <button 
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 -mr-2 text-slate-600"
        >
          <Menu className="w-5 h-5" />
        </button>
      </header>
      
      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed top-0 left-0 h-screen w-[280px] bg-white z-50 transform transition-transform duration-300 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <SidebarContent isMobile />
      </aside>
      
      {/* Spacer for content */}
      <div className={`hidden lg:block flex-shrink-0 transition-all duration-300 ${collapsed ? 'w-[70px]' : 'w-[240px]'}`} />
      <div className="lg:hidden h-14" />
    </>
  );
}
